import dynamic from 'next/dynamic';
import Link from 'next/link';
import styled, { css } from 'styled-components';
import { useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { IConstructorFacetRange } from 'shared/types/ConstructorFacet';
import {
  SearchFilterPillStyle,
  SearchFiltersHoverAndSelectedStyle,
} from 'search/styles/SearchStyles';
import { getAppliedFilters } from 'shared/utils/gridEventTrackingUtils';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { variableFontRegular } from 'styles/theme/text';
import { useEventContext } from 'tracking/hooks/useEventContext';
import { t } from 'localization/utils/t';
import { getConstructorDescription } from 'search/enums/translations';

const TP = 'search.components.searchGrid';

const SearchFilterTooltip = dynamic(
  import('search/components/searchGrid/SearchFilterTooltip').then((mod) => mod.SearchFilterTooltip),
  { ssr: false },
);

interface ISearchFilterInstantShipDesktop {
  facet: IConstructorFacetRange;
  resultIds: Array<string>;
}

export const SearchFilterInstantShip = ({ facet, resultIds }: ISearchFilterInstantShipDesktop) => {
  const { query, pathname } = useRouter();
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  const isInstantApplied =
    typeof facet.status?.min === 'number' && typeof facet.status?.max === 'number';
  const eventTrackingEventContext = useEventContext();

  const getUpdatedQuery = () => {
    const updatedQuery = { ...query };

    if (isInstantApplied) {
      delete updatedQuery.instant_ship_lowest_price_cents;
    } else {
      updatedQuery.instant_ship_lowest_price_cents = `${facet.min}-${facet.max}`;
    }

    return updatedQuery;
  };

  const handleMouseOver = () => setIsVisible(true);

  const handleMouseLeave = () => setIsVisible(false);

  const handleOnClick = () => {
    sendTrackingEvent('FILTER_APPLY_TAP', {
      event_context: eventTrackingEventContext,
      filter_name: facet.name,
      filter_selection: !isInstantApplied,
      result_id: resultIds[0],
      ...(getAppliedFilters(query) && { applied_filters: getAppliedFilters(query) }),
      ...(query?.query && { search_string: query?.query.toString() }),
    });
  };

  return (
    <Link
      href={{
        pathname,
        query: getUpdatedQuery(),
      }}
      passHref
      prefetch={false}
      scroll
      shallow
    >
      <SearchFilterInstantShip.InstantLink
        $isSelected={isInstantApplied}
        data-qa="instant_ship_filter"
        ref={ref}
        onMouseOver={handleMouseOver}
        onMouseLeave={handleMouseLeave}
        onClick={handleOnClick}
      >
        {t(`${TP}.instant`, 'Instant')}
        {facet.data.description && (
          <SearchFilterTooltip
            data-qa="instant_ship_tooltip"
            isVisible={isVisible}
            text={getConstructorDescription(facet.data.description)}
            triggerRef={ref}
          />
        )}
      </SearchFilterInstantShip.InstantLink>
    </Link>
  );
};

SearchFilterInstantShip.InstantLink = styled.a<{ $isSelected?: boolean }>`
  ${SearchFilterPillStyle};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${variableFontRegular({ size: '11.5px', height: '14px' })};
    border: none;
    display: block;
    margin: 0;
    padding: 0 0 4px;
    text-align: right;
    width: 100%;
    ${({ $isSelected }) =>
      $isSelected &&
      css`
        ${SearchFiltersHoverAndSelectedStyle};
        color: ${({ theme }) => theme.navigation.textColor};
        background-color: ${({ theme }) => theme.navigation.backgroundColor};
      `};

    :hover {
      ${SearchFiltersHoverAndSelectedStyle};
      color: ${({ theme }) => theme.navigation.textColor};
    }
  }
`;
